/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   PageHelpPanel.tsx
 *     Description     :   Slide-out contextual help drawer for complex pages
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-16
 *     Modified        :   2026-02-16 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.12.0 (Enterprise Dependency Architecture)
 *     Feature         :   135-ui-data-alignment
 * =========================================================================================
 *
 *  Right-side drawer triggered by a `?` button in page headers. Accepts
 *  structured help content (purpose, key concepts, how-to steps, related
 *  links) and renders it in an accessible, animated panel.
 *
 *  @module ui
 *  @spec 135-ui-data-alignment
 *  @story US6 - Self-Service Help
 */

import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { HelpCircle, X, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import { AppTooltip } from './AppTooltip';

// =============================================================================
// TYPES
// =============================================================================

export interface HelpConcept {
  /** Term being defined */
  term: string;
  /** Definition / explanation */
  definition: string;
}

export interface HelpStep {
  /** Step number (displayed) */
  step: number;
  /** Short instruction */
  instruction: string;
}

export interface HelpLink {
  /** Display label */
  label: string;
  /** Route path (internal) or URL (external) */
  href: string;
  /** Whether this is an external link (default: false) */
  external?: boolean;
}

export interface HelpSection {
  /** Section header */
  title: string;
  /** Free-form paragraph(s) */
  paragraphs?: string[];
  /** List of key concepts with definitions */
  concepts?: HelpConcept[];
  /** Numbered how-to steps */
  steps?: HelpStep[];
  /** Related page / doc links */
  links?: HelpLink[];
  /** Start collapsed (default: false) */
  defaultCollapsed?: boolean;
}

export interface PageHelpContent {
  /** Page title shown at the top of the panel */
  pageTitle: string;
  /** One-sentence purpose statement */
  purpose: string;
  /** Expandable content sections */
  sections: HelpSection[];
}

export interface PageHelpPanelProps {
  /** Structured help content for the current page */
  content: PageHelpContent;
  /**
   * @deprecated Ignored: 7b06e6505 gave every screen the same 40px trigger button. Still
   * accepted because existing callers pass it.
   */
  buttonSize?: 'sm' | 'md';
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * PageHelpPanel — Slide-out contextual help drawer.
 *
 * Renders a `?` button that opens a right-side drawer with structured help.
 */
export const PageHelpPanel: React.FC<PageHelpPanelProps> = ({ content, buttonSize = 'md' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Set<number>>(() => {
    const initial = new Set<number>();
    content.sections.forEach((s, i) => {
      if (s.defaultCollapsed) initial.add(i);
    });
    return initial;
  });

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const close = useCallback(() => setIsOpen(false), []);

  const toggleSection = useCallback((index: number) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, close]);

  return (
    <>
      {/* Trigger Button */}
      <AppTooltip title="Open contextual help for this page">
        <button
          onClick={toggle}
          className='h-10 w-10 shrink-0 flex items-center justify-center rounded-full text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors'
          aria-label='Open help panel'
          aria-expanded={isOpen}
        >
          <HelpCircle className='h-6 w-6 shrink-0' />
        </button>
      </AppTooltip>

      {/* Backdrop + drawer are PORTALED to <body> so `position: fixed` escapes
          any transformed/overflow ancestor (e.g. recharts containers on insight
          pages), which would otherwise clip the panel into a thin sliver. */}
      {createPortal(
        <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className='fixed inset-0 bg-black/40 dark:bg-black/40 z-[1040] transition-opacity'
          onClick={close}
          aria-hidden='true'
        />
      )}

      {/* Drawer */}
      <aside
        className={`
          fixed top-0 right-0 h-full w-96 max-w-[90vw] bg-[var(--color-bg-card)]
          shadow-2xl z-[1050] transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
        role='dialog'
        aria-label='Page help'
        aria-hidden={!isOpen}
      >
        {/* Content mounts only when open — keeps the drawer's title/sections out
            of the DOM while closed (no off-screen duplicate text). */}
        {isOpen && (
        <>
        {/* Panel Header */}
        <div className='flex items-center justify-between px-6 py-4 border-b border-[var(--color-border)]'>
          <div>
            <h2 className='text-lg font-semibold text-[var(--color-text-primary)]'>
              {content.pageTitle}
            </h2>
            <p className='text-sm text-[var(--color-text-secondary)] mt-0.5'>{content.purpose}</p>
          </div>
          <button
            onClick={close}
            className='w-8 h-8 rounded-full flex items-center justify-center text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-subtle)] transition-colors'
            aria-label='Close help panel'
          >
            <X className='w-5 h-5' />
          </button>
        </div>

        {/* Panel Body */}
        <div className='overflow-y-auto h-[calc(100%-80px)] px-6 py-4 space-y-5'>
          {content.sections.map((section, idx) => {
            const isCollapsed = collapsedSections.has(idx);

            return (
              <div key={idx} className='border-b border-[var(--color-border)] pb-4 last:border-0'>
                {/* Section Header (collapsible) */}
                <button
                  onClick={() => toggleSection(idx)}
                  className='flex items-center gap-2 w-full text-left text-sm font-semibold text-[var(--color-text-primary)] mb-2 hover:text-[var(--color-accent)] transition-colors'
                  aria-expanded={!isCollapsed}
                >
                  {isCollapsed ? (
                    <ChevronRight className='w-4 h-4 shrink-0' />
                  ) : (
                    <ChevronDown className='w-4 h-4 shrink-0' />
                  )}
                  {section.title}
                </button>

                {!isCollapsed && (
                  <div className='pl-6 space-y-3'>
                    {/* Paragraphs */}
                    {section.paragraphs?.map((p, i) => (
                      <p key={i} className='text-sm text-[var(--color-text-secondary)] leading-relaxed'>
                        {p}
                      </p>
                    ))}

                    {/* Key Concepts */}
                    {section.concepts && section.concepts.length > 0 && (
                      <dl className='space-y-2'>
                        {section.concepts.map((c, i) => (
                          <div key={i}>
                            <dt className='text-sm font-medium text-[var(--color-text-primary)]'>{c.term}</dt>
                            <dd className='text-sm text-[var(--color-text-secondary)] ml-3'>{c.definition}</dd>
                          </div>
                        ))}
                      </dl>
                    )}

                    {/* How-To Steps */}
                    {section.steps && section.steps.length > 0 && (
                      <ol className='space-y-2'>
                        {section.steps.map((s) => (
                          <li key={s.step} className='flex gap-2 text-sm text-[var(--color-text-secondary)]'>
                            <span className='text-[var(--color-accent)] font-semibold shrink-0'>
                              {s.step}.
                            </span>
                            {s.instruction}
                          </li>
                        ))}
                      </ol>
                    )}

                    {/* Related Links */}
                    {section.links && section.links.length > 0 && (
                      <ul className='space-y-1'>
                        {section.links.map((link, i) =>
                          link.external ? (
                            <li key={i}>
                              <a
                                href={link.href}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='inline-flex items-center gap-1 text-sm text-[var(--color-accent)] hover:underline'
                              >
                                {link.label}
                                <ExternalLink className='w-3 h-3' />
                              </a>
                            </li>
                          ) : (
                            <li key={i}>
                              <Link
                                to={link.href}
                                onClick={close}
                                className='text-sm text-[var(--color-accent)] hover:underline'
                              >
                                {link.label}
                              </Link>
                            </li>
                          )
                        )}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </>
        )}
      </aside>
        </>,
        document.body
      )}
    </>
  );
};

export default PageHelpPanel;
